import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Business,
  CalendarToday,
  Cancel,
  CheckCircle,
  Edit,
  Email,
  Language,
  LocationOn,
  Lock,
  Person,
  Phone,
  PhotoCamera,
  Save,
  Shield,
  Verified,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { doc, getDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import toast from 'react-hot-toast';
import { auth, db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { uploadAdminFile } from '../../services/firebase/storage';
import { friendlyError } from '../../utils/userMessages';
import BrandedPageLoader from '../common/BrandedPageLoader';
import {
  WorkspacePage,
  WorkspacePanel,
  PanelTitle,
  alpha,
  fieldSx,
  primaryButtonSx,
  quietButtonSx,
} from '../common/AdminWorkspace';

const heroColors = {
  blue: '#2F80ED',
  teal: '#16A085',
  purple: '#8B5CF6',
  orange: '#F39A24',
  pink: '#D94BA8',
};

const InfoRow = ({ icon, label, value, accent = heroColors.blue }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: .75, minWidth: 0 }}>
    <Box sx={{ width: 32, height: 32, flex: '0 0 auto', borderRadius: '10px', display: 'grid', placeItems: 'center', color: accent, bgcolor: alpha(accent, .085), border: `1px solid ${alpha(accent, .11)}`, '& svg': { fontSize: 16 } }}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: '.55rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '.075em', color: '#8A99AA' }}>{label}</Typography>
      <Typography sx={{ mt: .15, fontSize: '.73rem', fontWeight: 760, color: '#26364B', overflowWrap: 'anywhere' }}>{value || 'Not provided'}</Typography>
    </Box>
  </Box>
);

const HeroMetric = ({ icon, label, value, accent }) => (
  <Box sx={{ minWidth: 0, minHeight: 66, p: 1.1, borderRadius: '13px', display: 'flex', alignItems: 'center', gap: .9, background: 'rgba(255,255,255,.105)', border: '1px solid rgba(255,255,255,.18)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,.12)', backdropFilter: 'blur(9px)' }}>
    <Box sx={{ width: 33, height: 33, borderRadius: '10px', flex: '0 0 auto', display: 'grid', placeItems: 'center', bgcolor: 'rgba(255,255,255,.14)', color: '#fff', border: '1px solid rgba(255,255,255,.14)', '& svg': { fontSize: 17 } }}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ color: 'rgba(255,255,255,.66)', fontSize: '.51rem', fontWeight: 900, letterSpacing: '.075em', textTransform: 'uppercase' }}>{label}</Typography>
      <Typography sx={{ color: '#fff', mt: .15, fontWeight: 880, fontSize: '.87rem', lineHeight: 1.15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</Typography>
    </Box>
    <Box sx={{ ml: 'auto', width: 5, height: 5, borderRadius: '50%', bgcolor: accent, boxShadow: `0 0 0 4px ${alpha(accent, .17)}` }} />
  </Box>
);

const ProfileField = ({ icon, ...props }) => (
  <TextField
    fullWidth
    size="small"
    {...props}
    sx={{
      ...fieldSx,
      '& .MuiOutlinedInput-root': { ...fieldSx['& .MuiOutlinedInput-root'], minHeight: props.multiline ? 'auto' : 44 },
      '& .MuiInputBase-input': { fontSize: '.76rem', fontWeight: 650, color: '#26364B' },
      '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: '#607086', opacity: 1 },
      '& .MuiFormHelperText-root': { ml: .3, mt: .45, fontSize: '.58rem' },
      ...props.sx,
    }}
    slotProps={{
      ...props.slotProps,
      input: {
        startAdornment: icon ? <InputAdornment position="start"><Box sx={{ color: 'var(--primary,#2E6F8E)', display: 'grid', '& svg': { fontSize: 17 } }}>{icon}</Box></InputAdornment> : undefined,
        ...(props.slotProps?.input || {}),
      },
    }}
  />
);

const Profile = () => {
  const { user, userData, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [profileData, setProfileData] = useState({
    displayName: '', email: '', phoneNumber: '', role: '', companyName: '', companyId: '',
    bio: '', location: '', website: '', profilePicture: '', isVerified: false, isActive: false,
    createdAt: null, updatedAt: null,
  });
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const loadUserData = async () => {
    if (!user?.uid) return;
    setLoading(true);
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const data = userDoc.exists() ? userDoc.data() : {};
      setProfileData({
        displayName: data.displayName || user.displayName || '',
        email: data.email || user.email || '',
        phoneNumber: data.phoneNumber || user.phoneNumber || '',
        role: data.role || userData?.role || 'super_admin',
        companyName: data.companyName || '',
        companyId: data.companyId || '',
        bio: data.bio || '',
        location: data.location || '',
        website: data.website || '',
        profilePicture: data.profilePicture || data.photoURL || user.photoURL || '',
        isVerified: data.isVerified ?? user.emailVerified ?? false,
        isActive: data.isActive !== undefined ? data.isActive : true,
        createdAt: data.createdAt || user.metadata?.creationTime || null,
        updatedAt: data.updatedAt || null,
      });
    } catch (error) {
      console.error('Error loading profile:', error);
      toast.error(friendlyError(error, 'Unable to load your profile.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && user) loadUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user?.uid]);

  const handleSaveProfile = async () => {
    if (!user?.uid) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        displayName: profileData.displayName.trim(),
        phoneNumber: profileData.phoneNumber.trim(),
        bio: profileData.bio.trim(),
        location: profileData.location.trim(),
        website: profileData.website.trim(),
        updatedAt: serverTimestamp(),
      });
      toast.success('Profile updated successfully');
      setEditMode(false);
      await loadUserData();
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error(friendlyError(error, 'Unable to save profile changes.'));
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !user?.uid) return;
    if (!file.type?.startsWith('image/')) {
      toast.error('Please choose an image file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Profile image must be smaller than 5 MB.');
      return;
    }
    setUploadingAvatar(true);
    try {
      const asset = await uploadAdminFile({ file, scope: 'user', userId: user.uid, folder: 'profile-photos', kind: 'image' });
      if (!asset?.url) throw new Error('Upload did not return an image URL.');
      await updateDoc(doc(db, 'users', user.uid), { profilePicture: asset.url, updatedAt: serverTimestamp() });
      setProfileData((current) => ({ ...current, profilePicture: asset.url }));
      toast.success('Profile picture updated');
    } catch (error) {
      console.error('Profile picture upload failed:', error);
      toast.error(friendlyError(error, 'Unable to upload profile picture.'));
    } finally {
      setUploadingAvatar(false);
    }
  };

  const resetPasswordDialog = () => {
    setOpenPasswordDialog(false);
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  const handleChangePassword = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast.error('Please complete all password fields.');
      return;
    }
    if (passwordData.newPassword.length < 8) {
      toast.error('New password must be at least 8 characters.');
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match.');
      return;
    }
    setChangingPassword(true);
    try {
      const credential = EmailAuthProvider.credential(user.email, passwordData.currentPassword);
      await reauthenticateWithCredential(auth.currentUser || user, credential);
      await updatePassword(auth.currentUser || user, passwordData.newPassword);
      toast.success('Password changed successfully');
      resetPasswordDialog();
    } catch (error) {
      console.error('Password change failed:', error);
      toast.error(friendlyError(error, 'Unable to change password.'));
    } finally {
      setChangingPassword(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return 'Not available';
    try {
      const date = value?.toDate ? value.toDate() : new Date(value);
      if (Number.isNaN(date.getTime())) return 'Not available';
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (_) {
      return 'Not available';
    }
  };

  const initials = String(profileData.displayName || profileData.email || 'A').split(/\s+/).slice(0, 2).map((part) => part[0] || '').join('').toUpperCase();
  const roleLabel = String(profileData.role || 'super_admin').replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

  if (authLoading || loading) return <BrandedPageLoader label="Loading profile…" />;

  return (
    <WorkspacePage maxWidth={1580} sx={{ pb: 3 }}>
      <Box sx={{ display: 'grid', gap: 1.35 }}>
        <motion.div initial={{ opacity: 0, y: 9 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .3 }}>
          <Box
            sx={{
              position: 'relative',
              overflow: 'hidden',
              borderRadius: { xs: '16px', md: '18px' },
              minHeight: { xs: 265, md: 215 },
              p: { xs: 2, sm: 2.4, md: 2.7 },
              color: '#fff',
              background: 'linear-gradient(115deg, #166C8A 0%, #207C99 42%, #20A78E 100%)',
              border: '1px solid rgba(255,255,255,.18)',
              boxShadow: '0 14px 34px rgba(25,57,78,.15), inset 0 1px 0 rgba(255,255,255,.17)',
              '&::before': { content: '""', position: 'absolute', width: 300, height: 300, borderRadius: '50%', right: -105, top: -155, background: 'rgba(255,255,255,.08)' },
              '&::after': { content: '""', position: 'absolute', width: 240, height: 240, borderRadius: '50%', left: '44%', bottom: -185, background: 'rgba(255,255,255,.065)' },
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0,1.35fr) minmax(360px,.65fr)' }, gap: { xs: 2.2, md: 2.8 }, alignItems: 'center' }}>
              <Box sx={{ minWidth: 0, display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1.7, flexDirection: { xs: 'column', sm: 'row' } }}>
                <Box sx={{ position: 'relative', flex: '0 0 auto' }}>
                  <Avatar src={profileData.profilePicture || ''} sx={{ width: { xs: 76, md: 88 }, height: { xs: 76, md: 88 }, fontSize: '1.45rem', fontWeight: 900, color: '#fff', bgcolor: 'rgba(255,255,255,.15)', border: '4px solid rgba(255,255,255,.92)', boxShadow: '0 11px 28px rgba(9,45,60,.25)' }}>{initials || 'A'}</Avatar>
                  <Tooltip title="Change profile picture">
                    <IconButton component="label" disabled={uploadingAvatar} sx={{ position: 'absolute', right: -4, bottom: -4, width: 31, height: 31, bgcolor: '#fff', color: '#176D8C', border: '2px solid rgba(255,255,255,.95)', boxShadow: '0 6px 16px rgba(8,43,60,.18)', '&:hover': { bgcolor: '#F4FAFC' } }}>
                      {uploadingAvatar ? <CircularProgress size={15} /> : <PhotoCamera sx={{ fontSize: 16 }} />}
                      <input hidden accept="image/*" type="file" onChange={handleAvatarUpload} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '.57rem', fontWeight: 900, letterSpacing: '.11em', textTransform: 'uppercase', color: 'rgba(255,255,255,.68)' }}>TELCOFAIR · PLATFORM ADMINISTRATION</Typography>
                  <Typography sx={{ mt: .45, fontSize: { xs: '1.7rem', sm: '2rem', md: '2.25rem' }, lineHeight: 1.04, fontWeight: 900, letterSpacing: '-.045em', overflowWrap: 'anywhere' }}>{profileData.displayName || 'Administrator'}</Typography>
                  <Typography sx={{ mt: .65, maxWidth: 680, fontSize: { xs: '.72rem', md: '.78rem' }, lineHeight: 1.55, color: 'rgba(255,255,255,.77)' }}>{profileData.bio || 'Manage your TELCOFAIR platform identity, contact details and account security from one workspace.'}</Typography>
                  <Box sx={{ mt: 1.15, display: 'flex', flexWrap: 'wrap', gap: .6 }}>
                    <Chip size="small" icon={<Shield />} label={roleLabel} sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,.13)', border: '1px solid rgba(255,255,255,.17)', fontWeight: 800, '& .MuiChip-icon': { color: '#fff' } }} />
                    <Chip size="small" icon={profileData.isVerified ? <Verified /> : <Cancel />} label={profileData.isVerified ? 'Verified account' : 'Verification pending'} sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,.13)', border: '1px solid rgba(255,255,255,.17)', fontWeight: 800, '& .MuiChip-icon': { color: '#fff' } }} />
                    <Chip size="small" icon={<CheckCircle />} label={profileData.isActive ? 'Active' : 'Inactive'} sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,.13)', border: '1px solid rgba(255,255,255,.17)', fontWeight: 800, '& .MuiChip-icon': { color: '#fff' } }} />
                  </Box>
                </Box>
              </Box>

              <Box sx={{ minWidth: 0, display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: .75 }}>
                <HeroMetric icon={<CalendarToday />} label="Member Since" value={formatDate(profileData.createdAt)} accent="#9ED96C" />
                <HeroMetric icon={<Shield />} label="Role" value={roleLabel} accent="#B7A6FF" />
                <HeroMetric icon={<Verified />} label="Verification" value={profileData.isVerified ? 'Verified' : 'Pending'} accent="#66D6BD" />
                <HeroMetric icon={<CheckCircle />} label="Account" value={profileData.isActive ? 'Active' : 'Inactive'} accent="#FFD26A" />
              </Box>
            </Box>
          </Box>
        </motion.div>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(265px,.72fr) minmax(0,1.55fr)' }, gap: 1.25, alignItems: 'start' }}>
          <Box sx={{ display: 'grid', gap: 1.25 }}>
            <WorkspacePanel accent={heroColors.teal} sx={{ p: { xs: 1.35, md: 1.55 } }}>
              <PanelTitle title="Profile Snapshot" subtitle="Your current administrator identity" icon={<Person />} accent={heroColors.teal} />
              <Box sx={{ display: 'grid', gap: .1 }}>
                <InfoRow icon={<Email />} label="Email" value={profileData.email} accent={heroColors.blue} />
                <InfoRow icon={<Phone />} label="Phone" value={profileData.phoneNumber} accent={heroColors.teal} />
                <InfoRow icon={<LocationOn />} label="Location" value={profileData.location} accent={heroColors.orange} />
                <InfoRow icon={<Language />} label="Website" value={profileData.website} accent={heroColors.purple} />
                {profileData.companyName && <InfoRow icon={<Business />} label="Company" value={profileData.companyName} accent={heroColors.pink} />}
              </Box>
            </WorkspacePanel>

            <WorkspacePanel accent={heroColors.orange} sx={{ p: { xs: 1.35, md: 1.55 } }}>
              <PanelTitle title="Account Security" subtitle="Password and account protection" icon={<Lock />} accent={heroColors.orange} />
              <Typography sx={{ color: '#65758A', fontSize: '.68rem', lineHeight: 1.55, mb: 1.1 }}>Change your password after re-authenticating with your current credentials.</Typography>
              <Button fullWidth variant="outlined" startIcon={<Lock />} onClick={() => setOpenPasswordDialog(true)} sx={{ ...quietButtonSx, minHeight: 39 }}>Change Password</Button>
            </WorkspacePanel>
          </Box>

          <WorkspacePanel accent={heroColors.blue} sx={{ p: { xs: 1.45, md: 1.7 } }}>
            <PanelTitle
              title="Profile Information"
              subtitle="Keep your Super Admin profile current"
              icon={<Edit />}
              accent={heroColors.blue}
              action={!editMode ? (
                <Button size="small" variant="contained" startIcon={<Edit />} onClick={() => setEditMode(true)} sx={primaryButtonSx}>Edit Profile</Button>
              ) : (
                <Box sx={{ display: 'flex', gap: .65, flexWrap: 'wrap' }}>
                  <Button size="small" variant="outlined" startIcon={<Cancel />} onClick={() => { setEditMode(false); loadUserData(); }} sx={quietButtonSx}>Cancel</Button>
                  <Button size="small" variant="contained" startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save />} disabled={saving} onClick={handleSaveProfile} sx={primaryButtonSx}>{saving ? 'Saving…' : 'Save Changes'}</Button>
                </Box>
              )}
            />

            <Box sx={{ mt: 1.35, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,minmax(0,1fr))' }, gap: 1.1 }}>
              <ProfileField label="Display Name" value={profileData.displayName} onChange={(event) => setProfileData((current) => ({ ...current, displayName: event.target.value }))} disabled={!editMode} icon={<Person />} />
              <ProfileField label="Email Address" value={profileData.email} disabled icon={<Email />} helperText="Email address is managed by your authentication account" />
              <ProfileField label="Phone Number" value={profileData.phoneNumber} onChange={(event) => setProfileData((current) => ({ ...current, phoneNumber: event.target.value }))} disabled={!editMode} icon={<Phone />} placeholder="+91 …" />
              <ProfileField label="Location" value={profileData.location} onChange={(event) => setProfileData((current) => ({ ...current, location: event.target.value }))} disabled={!editMode} icon={<LocationOn />} placeholder="City, Country" />
              <Box sx={{ gridColumn: { xs: 'auto', sm: '1 / -1' } }}>
                <ProfileField label="Bio" value={profileData.bio} onChange={(event) => setProfileData((current) => ({ ...current, bio: event.target.value }))} disabled={!editMode} icon={<Person />} multiline rows={3} placeholder="Short administrator bio" />
              </Box>
              <Box sx={{ gridColumn: { xs: 'auto', sm: '1 / -1' } }}>
                <ProfileField label="Website" value={profileData.website} onChange={(event) => setProfileData((current) => ({ ...current, website: event.target.value }))} disabled={!editMode} icon={<Language />} placeholder="https://…" />
              </Box>
            </Box>

            {editMode && (
              <Box sx={{ mt: 1.15, p: .9, borderRadius: '11px', bgcolor: alpha(heroColors.blue, .045), border: `1px solid ${alpha(heroColors.blue, .09)}`, display: 'flex', alignItems: 'center', gap: .75 }}>
                <Edit sx={{ color: heroColors.blue, fontSize: 16 }} />
                <Typography sx={{ color: '#5D6E83', fontSize: '.63rem', fontWeight: 650 }}>Changes are saved to your Super Admin profile and reflected across the platform.</Typography>
              </Box>
            )}
          </WorkspacePanel>
        </Box>
      </Box>

      <Dialog open={openPasswordDialog} onClose={changingPassword ? undefined : resetPasswordDialog} fullWidth maxWidth="sm" slotProps={{ paper: { sx: { borderRadius: '20px', overflow: 'hidden' } } }}>
        <DialogTitle sx={{ p: 2, pb: 1.15 }}>
          <Box sx={{ display: 'flex', gap: .9, alignItems: 'center' }}>
            <Box sx={{ width: 36, height: 36, borderRadius: '11px', display: 'grid', placeItems: 'center', color: '#fff', background: `linear-gradient(145deg,${heroColors.orange},#F6B445)`, '& svg': { fontSize: 18 } }}><Lock /></Box>
            <Box><Typography sx={{ fontWeight: 850, color: '#182235', fontSize: '.95rem' }}>Change Password</Typography><Typography sx={{ color: '#7A899B', fontSize: '.62rem', mt: .15 }}>Confirm your current password first</Typography></Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: '12px !important', display: 'grid', gap: 1.05 }}>
          <ProfileField
            label="Current Password"
            type={showCurrentPassword ? 'text' : 'password'}
            value={passwordData.currentPassword}
            onChange={(event) => setPasswordData((current) => ({ ...current, currentPassword: event.target.value }))}
            icon={<Lock />}
            slotProps={{ input: { endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={() => setShowCurrentPassword((value) => !value)}>{showCurrentPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}</IconButton></InputAdornment> } }}
          />
          <ProfileField
            label="New Password"
            type={showNewPassword ? 'text' : 'password'}
            value={passwordData.newPassword}
            onChange={(event) => setPasswordData((current) => ({ ...current, newPassword: event.target.value }))}
            icon={<Lock />}
            helperText="Minimum 8 characters"
            slotProps={{ input: { endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={() => setShowNewPassword((value) => !value)}>{showNewPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}</IconButton></InputAdornment> } }}
          />
          <ProfileField
            label="Confirm New Password"
            type={showConfirmPassword ? 'text' : 'password'}
            value={passwordData.confirmPassword}
            onChange={(event) => setPasswordData((current) => ({ ...current, confirmPassword: event.target.value }))}
            icon={<Lock />}
            slotProps={{ input: { endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={() => setShowConfirmPassword((value) => !value)}>{showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}</IconButton></InputAdornment> } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, pt: .7 }}>
          <Button onClick={resetPasswordDialog} disabled={changingPassword} sx={quietButtonSx}>Cancel</Button>
          <Button variant="contained" onClick={handleChangePassword} disabled={changingPassword} startIcon={changingPassword ? <CircularProgress size={14} color="inherit" /> : <Lock />} sx={primaryButtonSx}>{changingPassword ? 'Changing…' : 'Change Password'}</Button>
        </DialogActions>
      </Dialog>
    </WorkspacePage>
  );
};

export default Profile;
